import { Link } from '@chakra-ui/next-js';
import { Box, Flex, Icon, Text } from '@chakra-ui/react';
import { HiArrowRight } from 'react-icons/hi';

import { Section } from '$components/layout/section';
import { FadeIn } from '$components/ui/fade-in';

export interface WritingPost {
  slug: string;
  title: string;
  description: string;
  createdAt: string;
  readingMinutes?: number;
}

interface WritingProps {
  posts: WritingPost[];
}

export function Writing({ posts }: WritingProps) {
  if (posts.length === 0) return null;

  return (
    <Section dataScroll="writing" eyebrow="Escrita" title="Notas e tutoriais.">
      <Box>
        {posts.slice(0, 3).map((post, index) => (
          <FadeIn key={post.slug} delay={index * 60}>
            <Link
              href={`/posts/${post.slug}`}
              role="group"
              display="block"
              py="20px"
              borderTopWidth="1px"
              borderColor="line.subtle"
              _last={{ borderBottomWidth: '1px' }}
              _hover={{ textDecoration: 'none' }}
              transition="background 0.25s ease"
              borderRadius="6px"
              px={{ md: '12px' }}
              mx={{ md: '-12px' }}
              sx={{ _hover: { bg: 'bg.surface' } }}
            >
              <Flex
                gap="14px"
                align="center"
                mb="6px"
                fontFamily="mono"
                fontSize="11px"
                letterSpacing="0.08em"
                color="text.faint"
                textTransform="uppercase"
              >
                <Text as="time">{post.createdAt}</Text>
                {post.readingMinutes && (
                  <>
                    <Box w="3px" h="3px" bg="text.faint" borderRadius="full" />
                    <Text>{post.readingMinutes} min</Text>
                  </>
                )}
              </Flex>

              <Text
                fontFamily="display"
                fontSize={{ base: '20px', md: '22px' }}
                fontWeight="500"
                color="text.primary"
                _groupHover={{ color: 'accent.500' }}
                transition="color 0.25s ease"
                mb="4px"
                lineHeight="1.2"
              >
                {post.title}
              </Text>

              <Text fontSize="14px" color="text.muted" noOfLines={1}>
                {post.description}
              </Text>
            </Link>
          </FadeIn>
        ))}
      </Box>

      <Flex justify="flex-end" mt="20px">
        <Link
          href="/posts"
          display="inline-flex"
          alignItems="center"
          gap="8px"
          fontFamily="mono"
          fontSize="12px"
          letterSpacing="0.08em"
          color="text.primary"
          _hover={{ color: 'accent.500', textDecoration: 'none' }}
          transition="color 0.2s ease"
        >
          Ver toda a escrita <Icon as={HiArrowRight} />
        </Link>
      </Flex>
    </Section>
  );
}
